import React, { useEffect, useState } from 'react';

import DeleteRounded from '@mui/icons-material/DeleteRounded';
import { Stack } from '@mui/material';
import { CirclePicker } from 'react-color';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';

import Breadcrumbs from '../../components/Breadcrumbs';
import Button from '../../components/Button';
import CommitForm from '../../components/CommitForm';
import ContentGroupsList from '../../components/ContentGroupsList';
import ContentWrapper from '../../components/ContentWrapper';
import DetailsModal, { Values } from '../../components/DetailsModal';
import Modal from '../../components/Modal';
import SkeletonStack from '../../components/SkeletonStack';
import {
  useDeleteTopicGroupMutation,
  useDeleteTopicMutation,
  useGetTopicGroupQuery,
  useNewTopicMutation,
  useUpdateTopicGroupMutation,
} from '../../services/topics';
import { SUBJECTS } from '../../utils/common/constants';
import useError from '../../utils/react/hooks/useError';
import useSuccess from '../../utils/react/hooks/useSuccess';

import {
  BreadcrumbsContainer,
  ColorPickerContainer,
  PageContainer,
} from './index.styles';

const INITIAL_TOPIC_FORM = {
  label: '',
};

interface PageContentsProps {
  subject: string;
  groupId: string;
}

const PageContents = ({ subject, groupId }: PageContentsProps) => {
  const navigate = useNavigate();

  const { data: topicGroup, isLoading } = useGetTopicGroupQuery(
    { subject, groupId }, { skip: !subject || !groupId },
  );

  const [newTopicForm, setNewTopicForm] = useState(INITIAL_TOPIC_FORM);
  const [newTopicFormOpen, setNewTopicFormOpen] = useState(false);
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [color, setColor] = useState<string | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<string | undefined>(undefined);

  const [newTopic, {
    isSuccess: isNewTopicSuccess,
    isLoading: isNewTopicLoading,
    error: newTopicError,
    reset: resetNewTopic,
  }] = useNewTopicMutation();
  const [updateTopicGroup, {
    isSuccess: isUpdateTopicGroupSuccess,
    isLoading: isUpdateTopicGroupLoading,
  }] = useUpdateTopicGroupMutation();
  const [deleteTopicGroup, {
    isSuccess: isDeleteTopicGroupSuccess,
    isLoading: isDeleteTopicGroupLoading,
    isError: isDeleteTopicGroupError,
    error: deleteTopicGroupError,
  }] = useDeleteTopicGroupMutation();
  const [deleteTopic, {
    isSuccess: isDeleteTopicSuccess,
    isLoading: isDeleteTopicLoading,
    isError: isDeleteTopicError,
    error: deleteTopicError,
  }] = useDeleteTopicMutation();

  const resetModal = async () => {
    await resetNewTopic();
    setNewTopicForm(INITIAL_TOPIC_FORM);
  };

  const closeModal = async () => {
    setColorModalOpen(false);
    setColor(topicGroup?.data.color);
  };

  const onTopicCreate = (values?: Values) => {
    newTopic({
      subject,
      topicGroup: groupId,
      label: values?.label,
    });
  };

  // Set default color once topic group is fetched
  useEffect(() => {
    setColor(topicGroup?.data.color);
  }, [topicGroup]);

  // Close color modal once topic group is updated
  useEffect(() => {
    if (isUpdateTopicGroupSuccess) {
      setColorModalOpen(false);
    }
  }, [isUpdateTopicGroupSuccess]);

  // Navigate out once the topic group is deleted
  useEffect(() => {
    if (isDeleteTopicGroupSuccess) {
      navigate('/admin/topics');
    }
  }, [isDeleteTopicGroupSuccess]);

  // Close modal after topic is created
  useEffect(() => {
    if (isNewTopicSuccess) {
      setNewTopicFormOpen(false);
      resetModal();
    }
  }, [isNewTopicSuccess]);

  useError(isDeleteTopicError, deleteTopicError);
  useError(isDeleteTopicGroupError, deleteTopicGroupError);
  useSuccess(isDeleteTopicSuccess, 'Topic deleted');
  useSuccess(isDeleteTopicGroupSuccess, 'Topic group deleted');

  return (!isLoading && topicGroup?.data ? (
    <>
      <BreadcrumbsContainer>
        <Breadcrumbs
          title={topicGroup?.data.label}
          path={[
            {
              label: SUBJECTS[topicGroup?.data.subject]?.label || 'Exams',
              href: `/admin/subjects/${topicGroup?.data.subject}`,
            },
          ]}
        />
      </BreadcrumbsContainer>
      <ContentWrapper title="Topic Group Details">
        <CommitForm
          data={topicGroup?.data}
          metadata={topicGroup?.metadata}
          fields={{
            label: {
              xs: 12,
              md: 12,
            },
            badgeLabel: {
              xs: 12,
              md: 12,
            },
          }}
          onSubmit={async (values) => updateTopicGroup({
            subject,
            groupId,
            attributes: values,
          })}
          isSubmitLoading={isUpdateTopicGroupLoading}
          autoSubmit
        />
      </ContentWrapper>
      <ContentWrapper title="Actions">
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={() => setColorModalOpen(true)}
          >
            Edit Badge Color
          </Button>
          <Button
            color="error"
            size="medium"
            onClick={async () => deleteTopicGroup({ subject, groupId })}
            disabled={(Object.keys(topicGroup?.data.topics) || []).length > 0}
            loading={isDeleteTopicGroupLoading}
            startIcon={(<DeleteRounded />)}
          >
            Delete
          </Button>
        </Stack>
      </ContentWrapper>
      <ContentWrapper title="Topics">
        <ContentGroupsList
          columns={[
            {
              field: 'label',
              headerName: 'Label',
              width: 480,
            },
            {
              field: 'control',
              headerName: 'Control',
              width: 100,
              renderCell: ({ row }: any) => (
                <Button
                  color="error"
                  size="small"
                  variant="text"
                  onClick={async () => {
                    setDeleteTarget(row.id);
                    await deleteTopic({ subject, topicId: row.id });
                  }}
                  loading={isDeleteTopicLoading && deleteTarget === row.id}
                >
                  Delete
                </Button>
              ),
            },
          ]}
          data={(Object.values(topicGroup?.data.topics) || [])
            .map((x) => ({ ...x, id: x?.key }))}
          loading={isLoading}
          actions={{
            create: {
              fn: () => setNewTopicFormOpen(true),
              label: 'Add Topic',
            },
          }}
        />
      </ContentWrapper>
      <DetailsModal
        open={newTopicFormOpen}
        handleClose={() => setNewTopicFormOpen(false)}
        error={newTopicError}
        title="Add New Topic"
        fields={{
          label: {
            editable: true,
            type: 'text',
          },
        }}
        values={newTopicForm}
        buttons={[{
          fn: onTopicCreate,
          label: 'Add',
          loading: isNewTopicLoading,
        }]}
      />
      <Modal
        open={colorModalOpen}
        handleClose={closeModal}
        title="Edit Badge Color"
        buttons={[
          {
            label: 'Cancel',
            onClick: closeModal,
            color: 'error',
          },
          {
            label: 'Save',
            color: 'darkPrimary',
            onClick: () => updateTopicGroup({
              subject,
              groupId,
              attributes: {
                color,
              },
            }),
            loading: isUpdateTopicGroupLoading,
          },
        ]}
      >
        <ColorPickerContainer>
          <CirclePicker
            color={color}
            onChangeComplete={(c) => setColor(c.hex)}
          />
        </ColorPickerContainer>
      </Modal>
    </>
  ) : (
    <SkeletonStack />
  )
  );
};

const AdminTopicsPage = () => {
  const { subject, group } = useParams();

  if (!subject || !group) {
    return (
      <SkeletonStack />
    );
  }

  return (
    <PageContainer>
      <PageContents
        subject={subject}
        groupId={group}
      />
    </PageContainer>
  );
};

export default AdminTopicsPage;
